import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

interface CtaSectionProps {
  onBookCall: () => void;
}

export default function CtaSection({ onBookCall }: CtaSectionProps) {
  const ref = useScrollAnimation();

  return (
    <section id="contact" className="relative bg-black py-28 md:py-36 overflow-hidden border-t border-white/5">
      {/* Glow blob */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 60% 60% at 50% 100%, rgba(0,240,255,0.06) 0%, transparent 70%)',
        }}
      />

      {/* Grid lines decoration */}
      <div
        className="absolute inset-0 pointer-events-none opacity-3"
        style={{
          backgroundImage:
            'linear-gradient(rgba(0,240,255,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(0,240,255,0.08) 1px, transparent 1px)',
          backgroundSize: '80px 80px',
        }}
      />

      <div ref={ref} className="relative z-10 max-w-4xl mx-auto px-6 text-center">
        {/* Label */}
        <div className="fade-in-up mb-3 inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-electric-blue/25 bg-electric-blue/5">
          <span className="w-1.5 h-1.5 rounded-full bg-electric-blue animate-pulse-slow flex-shrink-0" />
          <span className="text-electric-blue text-xs font-semibold tracking-widest uppercase">
            The CGOeight System — AI-Powered Performance Marketing
          </span>
        </div>

        {/* Spots badge */}
        <div className="fade-in-up mb-6 inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/3">
          <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse flex-shrink-0" />
          <span className="text-white/40 text-xs font-medium tracking-wide">Only 4 spots open this month</span>
        </div>

        {/* Headline */}
        <h2
          className="fade-in-up font-display font-bold text-white leading-tight mb-5"
          style={{ fontSize: 'clamp(2.5rem, 5vw, 4.5rem)', transitionDelay: '0.1s' }}
        >
          Ready to <span className="text-electric-blue text-glow-blue">3X</span> Your<br />
          Business in 30 Days?
        </h2>

        {/* Sub text */}
        <p
          className="fade-in-up text-white/40 text-base md:text-lg leading-relaxed max-w-xl mx-auto mb-10"
          style={{ transitionDelay: '0.2s' }}
        >
          We only take 4 new clients per month. If you&apos;re serious about transforming your business, book your free AI strategy call now before the spots are gone.
        </p>

        {/* CTA */}
        <div
          className="fade-in-up flex flex-col sm:flex-row items-center justify-center gap-4"
          style={{ transitionDelay: '0.3s' }}
        >
          <button
            onClick={onBookCall}
            className="whitespace-nowrap px-10 py-4 bg-electric-blue text-black text-base font-bold rounded-md glow-blue glow-blue-hover transition-all duration-300 tracking-wide cursor-pointer"
          >
            Book Your Free AI Strategy Call
          </button>
        </div>

        {/* Disclaimer */}
        <p
          className="fade-in-up mt-6 text-white/18 text-xs tracking-wide"
          style={{ transitionDelay: '0.4s' }}
        >
          No contracts. No commitments. Just results — or we don&apos;t get paid.
        </p>
      </div>
    </section>
  );
}
